import { useDispatch, useSelector } from 'react-redux'
import { selectAuthCommonData, selectAuthData, selectEnergyAuthCommonData } from '@/containers/Login/AuthSelectors'
import { useTranslation } from 'react-i18next'
import useFormStatus from '@/containers/SubscriptionFormPageNew/SubscriptionForm/useFormStatus'
import {
  updateActiveAddress,
  updateAddressData,
  updateAddressesConfigData,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import {
  selectActiveAddress,
  selectAddressesData,
  selectCurrentAddressData,
  selectCurrentAddressErrors,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import { modulesConfig } from '../../config'
import { lazy } from 'react'
import { subscriptionFormActionsV2 } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormV2Sagas'
import { selectAddressesConfigData } from '../../SubscriptionForm/SubscriptionFormSelectors'
import useBusinessType from '@/shared/hooks/useBusinessType'
import { HOUSING_TYPE_APARTMENT } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/Common/constants'
import { useSnackbar } from 'baseui/snackbar'

const AddressSearchComponents = {
  fr: lazy(() => import('./Fields/FR/AddressSearch')),
  default: lazy(() => import('./Fields/Default/AddressSearch')),
}

const useAddress = () => {
  const { t } = useTranslation()
  const { enqueue } = useSnackbar()
  const dispatch = useDispatch()
  const { country } = useSelector(selectAuthData)
  const { residenceOptions, houseTypeOptions, heatingOptions, hotwaterOptions, cookingOptions } =
    useSelector(selectAuthCommonData)
  const { tenantTypes } = useSelector(selectEnergyAuthCommonData)
  const { isEditable } = useFormStatus()
  const {
    residenceType,
    tenantType,
    housingType,
    additionalInformation,
    streetName = '',
    streetNumber = '',
    city = '',
    zipcode = '',
    floor,
    apartmentNumber,
    province,
    isResident,
    surface,
    heatingEnergy,
    cookingEnergy,
    hotwaterEnergy,
    energyLandscape = {},
  } = useSelector(selectCurrentAddressData)
  const { isAllowedEnergyVertical } = useBusinessType({})

  const activeAddress = useSelector(selectActiveAddress)
  const addresses = useSelector(selectAddressesData)
  const {
    addressIsSaving,
    isCalculatingConsumption = false,
    showAddressFields,
    showEnergyLandscape,
  } = useSelector(selectAddressesConfigData)
  const errors = useSelector(selectCurrentAddressErrors)
  const currentAddressData = useSelector(selectCurrentAddressData)

  const streetPart = [streetNumber, streetName].filter(Boolean).join(' ')
  const cityPart = [zipcode, city].filter(Boolean).join(' ')

  const addressLabel = [streetPart, cityPart].filter(Boolean).join(', ')

  const updateAddress = addressData => {
    dispatch(updateAddressData({ data: addressData, id: activeAddress }))
  }

  const updateAddressEnergyLandscape = energyLandscapeData => {
    dispatch(
      updateAddressData({
        data: { energyLandscape: { ...energyLandscape, ...energyLandscapeData } },
        id: activeAddress,
      }),
    )
  }

  const updateAddressesConfigHandler = data => {
    dispatch(updateAddressesConfigData(data))
  }

  const updateActiveAddressHandler = id => dispatch(updateActiveAddress(id))

  const calculateZipcodeValidationWarning = () => {
    const { zipcode, electricityMeter = {}, gasMeter = {} } = currentAddressData || {}
    if (!zipcode) return false

    const normalizedZipcode = zipcode.toString().trim()

    const electricityZipcode = electricityMeter.pdlEligibilityData?.elec?.technicalData?.address?.zipcode
      ?.toString()
      .trim()
    if (electricityZipcode && electricityZipcode !== normalizedZipcode) {
      return true
    }

    const gasZipcode = gasMeter.pceEligibilityData?.gas?.deliveryPoint?.address?.postalCode?.toString().trim()
    if (gasZipcode && gasZipcode !== normalizedZipcode) {
      return true
    }

    return false
  }

  const hasZipcodeWarning = calculateZipcodeValidationWarning()
  const residenceOptionsArray = []
  const houseTypeOptionsArray = []
  const tenantTypeOptionsArray = []
  houseTypeOptions?.forEach(option => {
    houseTypeOptionsArray.push({
      [option]: option,
    })
  })

  residenceOptions?.forEach(option => {
    residenceOptionsArray.push({
      [option]: option,
    })
  })

  tenantTypes?.forEach(option => {
    tenantTypeOptionsArray.push({
      [option]: option,
    })
  })
  const addressesList = []
  Object.entries(addresses).forEach(([id, address]) => {
    if (id === 'config' || id === 'new') return
    addressesList[id] = address.label
  })

  const triggerCalculateConsumption = () => {
    dispatch({
      type: subscriptionFormActionsV2.CALCULATE_CONSUMPTION,
    })
  }

  const saveActiveAddress = () => {
    dispatch({
      type: subscriptionFormActionsV2.SAVE_ADDRESS,
      enqueue,
      successMessage: t('subscriptionForm:address_v2.address_saved_success'),
    })
  }

  const shouldShowMeterInformation = activeAddress || !addressesList.length
  const config = modulesConfig[country].address ?? {}
  const LocalizedAddressSearchComponent = AddressSearchComponents[country] ?? AddressSearchComponents.default

  return {
    t,
    residenceType,
    tenantType,
    housingType,
    additionalInformation,
    streetName,
    streetNumber,
    city,
    zipcode,
    floor,
    apartmentNumber,
    province,
    isResident,
    surface,
    heatingEnergy,
    heatingOptions,
    cookingEnergy,
    cookingOptions,
    hotwaterEnergy,
    hotwaterOptions,
    energyLandscape,
    addresses,
    addressesList,
    activeAddress,
    showIsResident: config.isResident ?? true,
    showProvince: config.province ?? true,
    showFloor: config.floor ?? true,
    showApartmentNumber: config.apartmentNumber ?? true,
    energyLandscapeAvailable: config.energyLandscape ?? true,
    isApartment: housingType === HOUSING_TYPE_APARTMENT,
    residenceOptionsArray,
    houseTypeOptionsArray,
    houseTypeOptions,
    tenantTypeOptionsArray,
    updateAddress,
    updateActiveAddressHandler,
    updateAddressesConfigHandler,
    updateAddressEnergyLandscape,
    saveActiveAddress,
    shouldShowMeterInformation,
    errors,
    addressIsSaving,
    LocalizedAddressSearchComponent,
    showAddressFields,
    showEnergyLandscape: showEnergyLandscape || (energyLandscape && Object.entries(energyLandscape).length > 0),
    isAllowedEnergyVertical,
    isEditable,
    hasZipcodeWarning,
    isCalculatingConsumption,
    triggerCalculateConsumption,
    addressLabel,
  }
}

export default useAddress
